
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Shield, Zap, Globe, Repeat, Store, FileText, Bot, Wallet, CreditCard, Fingerprint, Smartphone } from 'lucide-react';

const FeatureItem = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
    <div className="flex items-start gap-4 not-prose p-5 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-100 dark:border-white/5 hover:border-nobel-gold/50 transition-colors">
        <div className="flex-shrink-0 w-10 h-10 bg-stone-100 dark:bg-white/5 rounded-xl flex items-center justify-center text-stone-900 dark:text-white shadow-inner">
            <Icon size={20} />
        </div>
        <div>
            <h4 className="font-bold text-stone-900 dark:text-white !my-0 text-sm">{title}</h4>
            <p className="text-xs mt-1.5 !my-0 text-stone-500 dark:text-stone-400 leading-relaxed">{desc}</p>
        </div>
    </div>
);

export const CoreFeatures = () => (
  <div className="space-y-16 animate-fade-in">
    <section>
        <div className="flex justify-between items-end mb-8 border-b border-stone-100 dark:border-white/10 pb-4">
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">The Sovereign Stack</h3>
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Infrastructure</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
            <FeatureItem
                icon={Store}
                title="Merchant Dashboard"
                desc="Real-time command center for managing global sales, multi-chain inventory, and enterprise roles."
            />
            <FeatureItem
                icon={CreditCard}
                title="Ghost Card (Titanium)"
                desc="Physical aerospace-grade metal hardware linked to your vault for instant physical commerce."
            />
            <FeatureItem
                icon={FileText}
                title="Escrow Contracts"
                desc="Trustless, milestone-based smart contracts governing service delivery and fund release."
            />
            <FeatureItem
                icon={Bot}
                title="AI Agent (x402)"
                desc="A programmatic micropayment gateway enabling autonomous agents to pay for API computation."
            />
            <FeatureItem
                icon={Shield}
                title="Privacy Relay"
                desc="Zero-knowledge transaction validation that decouples physical identity from main vault addresses."
            />
            <FeatureItem
                icon={Zap}
                title="Atomic Settlement"
                desc="Unified engine for Solana Pay and Sui payments, delivering instant sub-second finality."
            />
            <FeatureItem
                icon={Repeat}
                title="LayerZero OFT"
                desc="Native cross-chain fungible tokens (OFT) allow GHOST to burn-and-mint seamlessly between Sui and Solana."
            />
        </div>
    </section>

    <section>
        <div className="flex justify-between items-end mb-8 border-b border-stone-100 dark:border-white/10 pb-4">
            <h3 className="text-2xl font-serif font-bold text-stone-900 dark:text-white">GhostWallet OS</h3>
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Consumer Client</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
            <FeatureItem
                icon={Wallet}
                title="Unified Portfolio"
                desc="Consolidated view of assets across Solana and Sui with real-time price oracles via Pyth."
            />
            <FeatureItem
                icon={Fingerprint}
                title="Biometric Gateway"
                desc="Hardware-level security using FaceID and Secure Enclave to authorize every on-chain interaction."
            />
            <FeatureItem
                icon={Smartphone}
                title="Mobile POS"
                desc="Transform any modern smartphone into a full-featured merchant terminal in seconds."
            />
            <FeatureItem
                icon={Repeat}
                title="Auto-Liquidity"
                desc="Dynamic swap engine powered by Jupiter and Cetus for instant stablecoin settlement."
            />
        </div>
    </section>
  </div>
);
